const recorderMimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

export const formatDuration = (seconds: number) => {
  const safe = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safe / 60).toString().padStart(2, '0');
  const remainder = (safe % 60).toString().padStart(2, '0');
  return `${minutes}:${remainder}`;
};

export const recorderMimeToExtension = (mimeType: string) => {
  const normalized = mimeType.toLowerCase();
  if (normalized.startsWith('audio/webm')) return 'webm';
  if (normalized.startsWith('audio/mp4')) return 'm4a';
  if (normalized.startsWith('audio/mp3')) return 'mp3';
  if (normalized.startsWith('audio/wav') || normalized.startsWith('audio/x-wav')) return 'wav';
  if (normalized.startsWith('audio/ogg')) return 'ogg';
  if (normalized.startsWith('audio/aac')) return 'aac';
  if (normalized.startsWith('audio/flac')) return 'flac';
  return 'webm';
};

export const recorderMimeType = () => {
  if (typeof window.MediaRecorder?.isTypeSupported !== 'function') return 'audio/webm';
  return recorderMimeTypes.find(type => window.MediaRecorder.isTypeSupported(type)) || 'audio/webm';
};

// Human-friendly duration for headers/chips, e.g. "1h 5m", "18m 42s", "40s".
// Distinct from formatDuration (mm:ss), which is for the live recording timer.
export const formatDurationLabel = (seconds: number) => {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

// Reads an imported audio/video file's duration client-side so it can be
// stored as recording metadata, the same way a live capture already knows
// its own duration from the recorder's timer.
export const getFileDurationSeconds = (file: File): Promise<number | null> =>
  new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const audio = document.createElement('audio');
    let settled = false;

    const finish = (value: number | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      URL.revokeObjectURL(url);
      resolve(value);
    };

    const timeoutId = window.setTimeout(() => finish(null), 8000);
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => finish(Number.isFinite(audio.duration) ? Math.round(audio.duration) : null);
    audio.onerror = () => finish(null);
    audio.src = url;
  });
